<template>
    <Teleport to="body">
        <Transition name="sidebar-fade">
            <button v-if="open" type="button" class="fixed inset-0 z-40 w-full cursor-default bg-black/40 md:hidden" aria-label="Close goal form" @click="handleCancel" />
        </Transition>
        <Transition name="sidebar-slide">
            <div
                v-if="open"
                class="fixed inset-x-0 bottom-0 z-50 flex w-full max-h-[90dvh] flex-col rounded-t-2xl border-t border-[#e2e8f0] bg-[#fafafa] shadow-2xl pb-[var(--mobile-nav-total-height,0px)] md:inset-y-0 md:right-0 md:left-auto md:w-[440px] md:max-h-none md:rounded-none md:border-t-0 md:border-l md:pb-0"
                role="dialog"
                aria-modal="true">
                <!-- Header -->
                <div class="flex items-center justify-between gap-3 px-6 py-5 border-b border-[#e2e8f0]">
                    <h2 class="body text-valence-grey-900 min-w-0 flex-1 truncate">
                        {{ isEdit ? "Edit Goal" : "New Goal" }}
                    </h2>
                    <button
                        type="button"
                        class="shrink-0 flex h-6 w-6 items-center justify-center rounded text-valence-grey-500 hover:bg-slate-100"
                        aria-label="Close"
                        @click="handleCancel">
                        <i class="bi bi-x-lg text-sm" aria-hidden="true"></i>
                    </button>
                </div>

                <!-- Body -->
                <div class="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                    <!-- Title -->
                    <div class="flex flex-col gap-2">
                        <label for="goal-title-input" class="w-full flex flex-col gap-2">
                            <span class="scaffold-body-small text-valence-grey-750">Title</span>
                            <input
                                id="goal-title-input"
                                v-model="form.title"
                                type="text"
                                placeholder="What do you want to achieve?"
                                class="w-full rounded-md border border-valence-grey-250 bg-white px-3 py-2.5 body-small text-valence-grey-900 placeholder:text-valence-grey-400 focus:outline-none focus:ring-1 focus:ring-valence-primary-400" />
                        </label>
                    </div>

                    <!-- Description -->
                    <div class="flex flex-col gap-2">
                        <label for="goal-description-textarea" class="w-full flex flex-col gap-2">
                            <span class="scaffold-body-small text-valence-grey-750">Description</span>
                            <textarea
                                id="goal-description-textarea"
                                v-model="form.description"
                                rows="7"
                                placeholder="Additional details will help Nadia coach you toward success."
                                class="w-full resize-none rounded-md border border-valence-grey-250 bg-white px-3 py-2.5 body-small text-valence-grey-900 placeholder:text-valence-grey-400 focus:outline-none focus:ring-1 focus:ring-valence-primary-400" />
                        </label>
                    </div>

                    <!-- Assignee -->
                    <div v-if="requireConnection || fixedConnection" class="flex flex-col gap-2">
                        <span class="scaffold-body-small text-valence-grey-750">Assignee</span>

                        <!-- Fixed (read-only) -->
                        <div v-if="fixedConnection" class="flex flex-col gap-0.5">
                            <p class="nadia-title-subsection text-valence-grey-850">{{ fixedConnection.name }}</p>
                            <p v-if="fixedConnection.job_title" class="body-small text-valence-grey-750">{{ fixedConnection.job_title }}</p>
                        </div>

                        <!-- Selectable -->
                        <template v-else>
                            <div v-if="form.connection" class="flex items-start justify-between gap-2">
                                <div class="flex flex-col gap-0.5">
                                    <p class="nadia-title-subsection text-valence-grey-850">{{ form.connection.name }}</p>
                                    <p v-if="form.connection.job_title" class="body-small text-valence-grey-750">{{ form.connection.job_title }}</p>
                                </div>
                                <button type="button" class="shrink-0 details text-valence-grey-500 hover:text-valence-grey-800 mt-1" @click="form.connection = null">
                                    Change
                                </button>
                            </div>
                            <ConnectionSearch v-else @select="form.connection = $event" />
                        </template>
                    </div>
                </div>

                <!-- Priority toggle (new personal goals only) -->
                <div v-if="canPrioritize && !isEdit" class="px-6 pb-4">
                    <GoalPriorityToggleButton :is-prioritized="form.isPriority" prioritized-text="Make this a priority goal" @click="form.isPriority = !form.isPriority" />
                </div>

                <!-- Footer -->
                <div class="border-t border-[#e2e8f0]">
                    <div class="flex items-center justify-end gap-3 px-6 py-4">
                        <BaseButton theme="neutral" size="sm" :disabled="isSubmitting" @click="handleCancel">Cancel</BaseButton>
                        <BaseButton theme="primary" size="sm" :disabled="!isValid || isSubmitting" @click="handleSubmit">
                            <i v-if="isSubmitting" class="bi bi-arrow-clockwise animate-spin" aria-hidden="true"></i>
                            {{ isEdit ? "Save changes" : "Add goal" }}
                        </BaseButton>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { type GoalConnection, type GoalCreateEditData, GoalType } from "~vue/types/goals";
import { computed, reactive, watch } from "vue";

import ConnectionSearch from "./ConnectionSearch.vue";
import GoalPriorityToggleButton from "./GoalPriorityToggleButton.vue";

const props = defineProps<{
    initialData?: Partial<GoalCreateEditData>;
    isSubmitting?: boolean;
    requireConnection?: boolean;
    fixedConnection?: GoalConnection | null;
    canPrioritize?: boolean;
}>();

const emit = defineEmits<{
    submit: [data: GoalCreateEditData];
    close: [];
}>();

const open = defineModel<boolean>("open", { default: false });

const isEdit = computed(() => !!props.initialData?.id);

const form = reactive({
    title: props.initialData?.title ?? "",
    description: props.initialData?.description ?? "",
    goal_type: (props.initialData?.goal_type ?? GoalType.Development) as GoalType,
    connection: (props.initialData?.connection ?? null) as GoalConnection | null,
    isPriority: false,
});

watch(
    open,
    (val) => {
        if (val) {
            resetForm();
        }
    },
    { flush: "post" },
);

watch(
    () => props.isSubmitting,
    (val, prev) => {
        if (prev === true && val === false) {
            open.value = false;
        }
    },
);

const isValid = computed(() => form.title.trim() !== "" && ((!props.requireConnection && !props.fixedConnection) || form.connection !== null));

function cleanDescription(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

function resetForm() {
    form.title = props.initialData?.title ?? "";
    form.description = cleanDescription(props.initialData?.description ?? "");
    form.goal_type = props.initialData?.goal_type ?? GoalType.Development;
    form.connection = props.fixedConnection ?? props.initialData?.connection ?? null;
    form.isPriority = props.initialData?.is_prioritized ?? false;
}

function handleCancel() {
    open.value = false;
    emit("close");
}

function handleSubmit() {
    if (!isValid.value) {
        return;
    }
    emit("submit", {
        id: props.initialData?.id,
        title: form.title.trim(),
        description: form.description.trim(),
        goal_type: form.goal_type,
        team_member_goal: props.initialData?.team_member_goal ?? false,
        connection_id: form.connection?.id ?? props.initialData?.connection_id ?? null,
        connection: form.connection ?? props.initialData?.connection ?? null,
        is_prioritized: props.canPrioritize && !isEdit.value ? form.isPriority : undefined,
    });
}
</script>

<style scoped>
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
    transform: translateY(100%);
}

@media (min-width: 768px) {
    .sidebar-slide-enter-from,
    .sidebar-slide-leave-to {
        transform: translateX(100%);
    }
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
    transition: transform 300ms ease;
}

.sidebar-fade-enter-from,
.sidebar-fade-leave-to {
    opacity: 0;
}

.sidebar-fade-enter-active,
.sidebar-fade-leave-active {
    transition: opacity 200ms ease;
}
</style>
